import React, { useRef, useEffect, useState } from 'react';
import { IVideoTrack } from '../../types';
import { styled } from '@mui/material/styles';
import { Track } from 'twilio-video';

const Video = styled('video')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

interface VideoTrackProps {
  track: IVideoTrack;
  isLocal?: boolean;
  priority?: Track.Priority | null;
}

export default function VideoTrack({ track, isLocal, priority }: VideoTrackProps) {
  const ref = useRef<HTMLVideoElement>(null!);
  const [isTrackEnabled, setIsTrackEnabled] = useState(track.isEnabled);

  useEffect(() => {
    const el = ref.current;
    el.muted = true;
    if (track.setPriority && priority) {
      track.setPriority(priority);
    }
    track.attach(el);
    return () => {
      track.detach(el);
      if (track.setPriority && priority) {
        // Passing `null` to setPriority will set the track's priority to that which it was published with.
        track.setPriority(null);
      }
    };
  }, [track, priority]);

  useEffect(() => {
    const handleEnabled = () => setIsTrackEnabled(true);
    const handleDisabled = () => setIsTrackEnabled(false);

    setIsTrackEnabled(track.isEnabled);
    track.on('enabled', handleEnabled);
    track.on('disabled', handleDisabled);

    return () => {
      track.off('enabled', handleEnabled);
      track.off('disabled', handleDisabled);
    };
  }, [track]);

  // The local video track is mirrored.
  const isFrontFacing = track.mediaStreamTrack.getSettings().facingMode !== 'environment';
  const style = isLocal && isFrontFacing ? { transform: 'rotateY(180deg)' } : {};
  const visibility = isTrackEnabled ? {} : { visibility: 'hidden' };

  return <Video ref={ref} style={{ ...style, ...visibility }} />;
}
